import {
    AfterViewInit,
    Component,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { TimestampDatePipe } from '../plants/shared/timestamp-date.pipe';
import { CityService } from '../plants/services/city.service';
import { SearchInputWithDebouncingComponent } from '../../ui-components/search-input-with-debouncing/search-input-with-debouncing.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { AddCityComponent } from './add-city/add-city.component';
import { UpdateCityComponent } from './update-city/update-city.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export type CityRow = {
    id: number;
    title: string;
    timeZoneId: string;
    latitude: number | null;
    longitude: number | null;
    disabled: boolean;
    CreatedAt: string;
    lastUpdate: string;
};

@Component({
    selector: 'app-cities-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatInputModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TimestampDatePipe,
        MatCardModule,
        SearchInputWithDebouncingComponent,
        MatButtonModule,
        MatTooltipModule,
        TranslatePipe
    ],
    templateUrl: './city-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./city-list.component.scss']
})
export class CitiesListComponent implements AfterViewInit {
    searchCtrl = new FormControl<string>('', { nonNullable: true });
    dataSource = new MatTableDataSource<CityRow>([]);
    displayedColumns: string[] = [
        'id',
        'title',
        'latitude',
        'longitude',
        'timeZoneId',
        'status',
        'CreatedAt',
        'lastUpdate',
        'action'
    ];

    loading = false;
    pageSize = 8;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        private citySvc: CityService,
        private dialog: MatDialog,
        private translate: TranslateService
    ) {}

    get dateLocale(): string {
        const lang = (this.translate.getCurrentLang() || 'en').toLowerCase();
        return lang.startsWith('da') ? 'da-DK' : 'en-GB';
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            this.paginator.pageSize = this.pageSize;
            this.dataSource.sort = this.sort;

            this.dataSource.sortingDataAccessor = (item, prop) => {
                switch (prop) {
                    case 'id':
                        return item.id;
                    case 'title':
                        return item.title?.toLowerCase() ?? '';
                    case 'timeZoneId':
                        return item.timeZoneId?.toLowerCase() ?? '';
                    case 'latitude':
                        return item.latitude ?? Number.NEGATIVE_INFINITY;
                    case 'longitude':
                        return item.longitude ?? Number.NEGATIVE_INFINITY;
                    case 'status':
                        return item.disabled ? 1 : 0;
                    case 'CreatedAt':
                        return Date.parse(item.CreatedAt || '') || 0;
                    case 'lastUpdate':
                        return Date.parse(item.lastUpdate || '') || 0;
                    default:
                        return (item as any)[prop];
                }
            };

            this.dataSource.filterPredicate = (row, filter) => {
                const f = filter.trim().toLowerCase();
                if (!f) return true;
                return (
                    String(row.id).includes(f) ||
                    (row.title || '').toLowerCase().includes(f) ||
                    (row.timeZoneId || '').toLowerCase().includes(f) ||
                    String(row.latitude ?? '').includes(f) ||
                    String(row.longitude ?? '').includes(f)
                );
            };

            this.searchCtrl.valueChanges
                .pipe(
                    startWith(this.searchCtrl.value),
                    debounceTime(300),
                    distinctUntilChanged()
                )
                .subscribe(q => {
                    this.dataSource.filter = (q ?? '').trim().toLowerCase();
                    if (this.paginator)
                        setTimeout(() => this.paginator.firstPage());
                });

            this.loadCities();
        });
    }

    private mapToRows(res: any): CityRow[] {
        const arr = Array.isArray(res) ? res : res?.cities ?? [];
        return arr.map((c: any) => ({
            id: c.id,
            title: c.title ?? '',
            timeZoneId: c.timeZoneId ?? '',
            latitude: this.toCoordinate(c.latitude),
            longitude: this.toCoordinate(c.longitude),
            disabled: !!c.disabled,
            CreatedAt: c.CreatedAt ?? c.createdAt ?? '',
            lastUpdate: c.lastUpdate ?? ''
        })) as CityRow[];
    }

    private toCoordinate(value: unknown): number | null {
        if (value === null || value === undefined || value === '') return null;

        const coordinate = Number(value);
        return Number.isFinite(coordinate) ? coordinate : null;
    }

    coordinateLabel(value: number | null, axis: 'lat' | 'lng'): string {
        if (value === null) return '—';

        const suffix = axis === 'lat'
            ? (value >= 0 ? 'N' : 'S')
            : (value >= 0 ? 'E' : 'W');

        return `${Math.abs(value).toFixed(5)}° ${suffix}`;
    }

    loadCities(): void {
        this.loading = true;
        this.citySvc.getCities('').subscribe({
            next: (res: any) => {
                this.dataSource.data = this.mapToRows(res);
                this.loading = false;
                this.paginator.firstPage();
            },
            error: () => {
                this.dataSource.data = [];
                this.loading = false;
            }
        });
    }

    onSearch(q: string): void {
        const query = (q ?? '').trim();
        this.searchCtrl.setValue(query, { emitEvent: true });
        if (this.paginator) this.paginator.firstPage();
    }

    onEdit(id: number): void {
        const row = this.dataSource.data.find(r => r.id === id);
        if (!row) return;

        const ref = this.dialog.open(UpdateCityComponent, {
            width: '720px',
            maxWidth: '92vw',
            data: {
                id: row.id,
                title: row.title,
                timeZoneId: row.timeZoneId,
                latitude: row.latitude,
                longitude: row.longitude
            },
            height: 'auto',
            maxHeight: '90vh',
            autoFocus: false,
            restoreFocus: false,
            backdropClass: 'dim-backdrop',
            panelClass: ['city-dialog']
        });

        ref.afterClosed().subscribe(
            (
                updated?: {
                    id: number;
                    title: string;
                    timeZoneId: string;
                    latitude: number | null;
                    longitude: number | null;
                } | null
            ) => {
                if (!updated) return;
                const copy = this.dataSource.data.slice();
                const idx = copy.findIndex(r => r.id === updated.id);
                if (idx > -1) {
                    copy[idx] = {
                        ...copy[idx],
                        title: updated.title,
                        timeZoneId: updated.timeZoneId,
                        latitude: updated.latitude,
                        longitude: updated.longitude
                    };
                    this.dataSource.data = copy;
                }
            }
        );
    }

    openAddCity(): void {
        const ref = this.dialog.open(AddCityComponent, {
            width: '720px',
            maxWidth: '92vw',
            height: 'auto',
            maxHeight: '90vh',
            autoFocus: false,
            restoreFocus: false,
            backdropClass: 'dim-backdrop',
            panelClass: ['city-dialog']
        });
        ref.afterClosed().subscribe(result => {
            if (!result) return;
            this.loadCities();
        });
    }
}
